// frontend/src/components/planning/ServiceCard/components/ReadingsSection.tsx

import React, { useState } from 'react';
import { ReadingsSectionProps } from '../types';
import componentStyles from '../styles/components.module.css';
import ReadingModal from '../../ReadingModal/ReadingModal';
import EditReadingsModal from '../../EditReadingsModal/EditReadingsModal';
import { fetchBiblePassage } from '../../../../services/readingService';
import { Service } from '../types/index';
import { Alert } from 'react-bootstrap';

const READING_ITEMS = [
  { id: 'ot', label: 'OT', key: 'ot_reading' },
  { id: 'psalm', label: 'Psalm', key: 'psalm_reading' },
  { id: 'nt', label: 'NT', key: 'nt_reading' },
  { id: 'gospel', label: 'Gospel', key: 'gospel_reading' }
] as const;

export const ReadingsSection: React.FC<ReadingsSectionProps> = ({
  serviceId,
  readings,
  liturgicalClass,
  liturgyName,
  liturgyDate,
  onReadingsUpdate // Add this prop for handling updates
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReadings, setSelectedReadings] = useState<Array<{ reference: string, text: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleReadingClick = async (reference: string | undefined) => {
    if (!reference || reference === 'Not set') return;

    try {
      console.log('Fetching bible passage for reference:', reference);
      setIsLoading(true);
      setError(null);
      setShowModal(true);

      const passages = await fetchBiblePassage(reference);
      console.log('Received passages:', passages);
      setSelectedReadings(passages);
    } catch (error) {
      console.error('Error fetching Bible passage:', error);
      setError('Failed to load Bible passage. Please try again.');
      setShowModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = () => {
    setError(null);
    setSuccessMessage(null);
    setShowEditModal(true);
  };

  const handleReadingsUpdate = (updatedService: Service) => {
    console.log('Readings updated:', updatedService);
    
    // Update the parent component with new service data
    if (onReadingsUpdate) {
      onReadingsUpdate(updatedService);
    }

    // Show success message
    setSuccessMessage('Readings updated successfully');
    
    // Clear success message after delay
    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  return (
    <div>
      <div className={componentStyles.sectionHeader}>
        <h4>Readings</h4>
        <i
          className={`bi bi-pencil-square ${componentStyles.editIcon}`}
          onClick={handleEditClick}
          title="Edit readings"
        />
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <div className={componentStyles.container}>
        {READING_ITEMS.map(item => {
          const reference = readings[item.key as keyof typeof readings];
          return (
            <div
              key={`reading-${serviceId}-${item.id}`}
              className={`${componentStyles.item} ${componentStyles[liturgicalClass]}`}
              onClick={() => handleReadingClick(reference)}
              style={{ 
                cursor: reference ? 'pointer' : 'default',
                opacity: isLoading ? 0.7 : 1 
              }}
            >
              <p className={componentStyles.itemLabel}>{item.label}</p>
              <p className={componentStyles.itemContent} style={reference ? undefined : { color: 'var(--cl-text-muted)' }}>
                {reference || 'Not set'}
              </p>
            </div>
          );
        })}
      </div>

      {/* View Reading Modal */}
      <ReadingModal
        show={showModal}
        onHide={() => setShowModal(false)}
        readings={selectedReadings}
        isLoading={isLoading}
        liturgyName={liturgyName}
        liturgyDate={liturgyDate}
      />

      {/* Edit Readings Modal */}
      <EditReadingsModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        readings={readings}
        serviceId={serviceId}
        liturgyName={liturgyName}
        liturgyDate={liturgyDate}
        onUpdate={handleReadingsUpdate}  // Add the update handler
      />
    </div>
  );
};